import { store } from '@redux/store';
import GoogleAnalyticsCustomDimensions from 'constants/googleAnalyticsCustomDimension';
import GoogleAnalyticsEvent from 'dataTransferObjects/googleAnalyticsEvent';
import { userProfileActions } from 'features/userProfile';
import { selectModal } from 'layout/redux/selectors';
import { closeButton, privacyPolicyParagraph } from '../constants/sharedHtml';
import leadConstructor from '../services/leadConstructor';
import { postLeadBrochureForm } from '../services/taskFlow';
import BrochureButton from './brochureButton';
import CommentsTextArea from './commentsTextArea';
import EmailInput from './emailInput';
import NameInput from './nameInput';
import PhoneInput from './phoneInput';
import RecommendedCheckbox from './recommendedCheckbox';
import ZipInput from './zipInput';
import RealtorDropdown from './realtorDropdown';
import ReCaptchaToken from './reCaptchaToken';
import LegalText from './legalText';

/* Creates a new instance of a Request Tour Form to allow users making an appointment to tour the selected housing. */
export default class BrochureFormPaid {
  constructor() {
    const state = store.getState();
    const userProfile = state?.userProfile || {};
    const { id, guid, type } = selectModal(state);
    this.id = id;
    this.guid = guid;
    this.type = type;
    this.excludeRecommendations = false;
    this.getBrochureButton = new BrochureButton();
    this.commentsTextArea = new CommentsTextArea();
    this.emailAddressInput = new EmailInput();
    this.emailAddressInput.value = userProfile.email;
    this.formId = 'brochureFormPaid';
    this.fullNameInput = new NameInput();
    this.fullNameInput.value = userProfile.name;
    this.phoneNumberInput = new PhoneInput();
    this.phoneNumberInput.value = userProfile.phone;
    this.postalCodeInput = new ZipInput();
    this.postalCodeInput.value = userProfile.zip;
    this.postalCodeInput.isInternational = userProfile.livesOutside;
    this.realtorDropdown = new RealtorDropdown();
    this.realtorDropdown.value = userProfile.realtorQuestion;
    this.recommendedCheckbox = new RecommendedCheckbox();
    this.recommendedCheckbox.value = userProfile.hasUserOptedIntoRecommendationEmail;
    this.onClickEvent = null;
    this.recaptchaToken = new ReCaptchaToken();
    this.legalText = new LegalText(privacyPolicyParagraph);
  }

  /**
   * validateNode: Check if the field has a value as expected.
   * @param {Object} node Input to validate
   * @returns A boolean value indicating whether the input is valid or not.
   */
  static validateNode(node) {
    return node.validate();
  }

  /**
   * getFormOpenedAnalyticsData: Gets the object required for the Google Analytics events on form opened.
   * @param {Object} properties Information regarding to the listing.
   * @returns The Google Analtic event information.
   */
  static getFormOpenedAnalyticsData(properties) {
    const customDimensions = {};

    if (properties.marketId) {
      customDimensions[[GoogleAnalyticsCustomDimensions.MarketId().getName()]] = properties.marketId;
    }

    if (properties.marketName && properties.stateAbbreviation) {
      customDimensions[[GoogleAnalyticsCustomDimensions.MarketName().getName()]] = `${properties.marketName}, ${properties.stateAbbreviation}`;
    }

    if (properties.city) {
      customDimensions[[GoogleAnalyticsCustomDimensions.City().getName()]] = properties.city;
    }

    return new GoogleAnalyticsEvent(
      "Lead Events",
      "Search Results CTA Free Brochure",
      "Open Form - Free Brochure",
      customDimensions);
  }

  /**
   * setHousingData: Sets the housing information for the form.
   * @param {Object} properties Information regarding to the listing.
   */
  setHousingData(properties = {}) {
    this.housingData = properties;
  }

  /**
   * submitBrochureForm: Process to validate and submit the form.
   * @param {event} event Click event.
   */
  async submitBrochureForm(event) {
    event.preventDefault();

    const validationFunctions = [
      this.emailAddressInput,
      this.fullNameInput,
      this.postalCodeInput,
      this.phoneNumberInput,
      this.realtorDropdown,
    ];

    this.getBrochureButton.getNode().disabled = 'disabled';

    const results = await Promise.all(
      validationFunctions.map((input) => BrochureFormPaid.validateNode(input))
    );

    if (results.some((isValid) => !isValid)) {
      this.getBrochureButton.getNode().disabled = false;
      return;
    }

    /**
     * RULE: we first validate regular form fields, then we validate reCAPTCHA only if all regular fields are valid.
     */
    const isValidRecaptcha = await this.recaptchaToken.validate();
    if (!isValidRecaptcha) {
      this.getBrochureButton.getNode().disabled = false;
      return;
    }

    const formData = new FormData(this.getForm());
    const brochureInformation = Object.fromEntries(formData.entries());

    brochureInformation.id = this.id;
    brochureInformation.type = `${this.type}-brochure`;
    brochureInformation.excludeRecommendations = this.excludeRecommendations;
    brochureInformation.fromPage = this.housingData.fromPage;
    brochureInformation.includeRecommended = !!brochureInformation.includeRecommended;
    brochureInformation.guid = this.guid;

    const brochureLeadObject = leadConstructor(brochureInformation);
    brochureLeadObject.setHousingInformation?.(this.housingData.listingInformation);

    postLeadBrochureForm(brochureLeadObject);
  }

  /**
   * handleEmailValidationResult: Handles the result of the email validation.
   * @param {Object} isValid The result of the email validation.
   */
  handleEmailValidationResult(isValid) {
    this.getBrochureButton.getNode().disabled = false;
    if (isValid) {
      this.emailAddressInput.setValidState(this.emailAddressInput.getNode());
      this.emailAddressInput.toggleEmailValidationDataAttribute(true);
    } else {
      this.emailAddressInput.setInvalidState(this.emailAddressInput.getNode());
      this.emailAddressInput.toggleEmailValidationDataAttribute(false);
    }
  }

  /**
   * Sets the binding required for the form.
   */
  bindEvents() {
    this.onClickEvent = this.submitBrochureForm.bind(this);
    this.phoneNumberInput.bindEvents();
    this.postalCodeInput.bindEvents();
    this.recaptchaToken.bindEvents();
    if (this.fullNameInput.value === '') {
      this.fullNameInput.focus();
    }
    this.realtorDropdown.setSelectedValue();
    this.getBrochureButton.getNode().addEventListener('click', this.onClickEvent);
    this.legalText.bindEvents();
    this.bindChangeEvents();
  }

  /**
   * bindChangeEvents: Get the form inputs then subscribe each OnChangeEvent to store and persist their values
   */
  bindChangeEvents() {
    const changeInputs = [
      this.emailAddressInput,
      this.fullNameInput,
      this.postalCodeInput,
      this.phoneNumberInput,
      this.recommendedCheckbox,
      this.realtorDropdown,
    ];

    changeInputs.forEach(input => input.getOnChangeEvent().subscribe(data =>
      store.dispatch(userProfileActions.update(data))));
  }

  /**
   * Removes the bindings that should not be still using.
   */
  unbindEvents() {
    this.getBrochureButton.getNode().removeEventListener('click', this.onClickEvent);
    this.legalText.unbindEvents();
    this.recaptchaToken?.unbindEvents();
  }

  /**
   * getForm: Gets the form container
   * @returns A HTML element related to the form container.
   */
  getForm() {
    return document.getElementById(this.formId);
  }

  /**
   * Gets the HTML structure for the brochure form paid.
   * @returns The string with the HTML structure.
   */
  getHtml() {
    this.legalText.setReplacements([
      { key: "{0}", value: "Get Brochure"},
      { key: "{1}", value: this.housingData.listingInformation?.brandName ?? "the builder" },
    ]);

    return `
    <div class="lead-form form--brochure" data-lead-form data-qa="lead_modal">
      ${closeButton}
      <p class="form__title">Free Detailed Brochure, Instantly!</p>
      <form id='${this.formId}'>
        <fieldset>
          <div class="form__field">
            ${this.fullNameInput.getHtml()}
          </div>
          <div class="form__field">
            ${this.emailAddressInput.getHtml()}
          </div>
          <div class="form__field">
            ${this.phoneNumberInput.getHtml()}
          </div>
          <div class="form__field field--split">
            ${this.postalCodeInput.getHtml()}
          </div>
          <div class="form__field agent--field">
            ${this.realtorDropdown.getHtml()}
          </div>
          <div class="form__field field--checkbox">
            ${this.recommendedCheckbox.getHtml()}
          </div>
          <div class="form__field">
            ${this.commentsTextArea.getHtml()}
          </div>
          <div class="form__button-bar">
            ${this.getBrochureButton.getHtml()}
          </div>
          <input type="hidden" id="leadAction" name="leadAction" value="RequestFreeBrochure" />
          ${this.recaptchaToken.getHtml()}
        </fieldset>        
      </form>
      ${this.legalText.getHtml()}
    </div>`;
  }
}
